import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, DateDecoder, EnumDecoder, field, IntegerDecoder, StringDecoder } from '@common/encoding';
import { v4 as uuidv4 } from 'uuid';
import { Formatter } from '@stamhoofd/utility';
import { getActivePeriodIds, getActivePeriods } from './getActivePeriods.js';
import { GroupGenderType } from './GroupGenderType.js';
import { GroupPrivateSettings } from './GroupPrivateSettings.js';
import { GroupSettings, WaitingListType } from './GroupSettings.js';
import { GroupType } from './GroupType.js';
import { Gender } from './members/Gender.js';
import { PermissionLevel } from '@stamhoofd/types/PermissionLevel';
import { PermissionRoleForResponsibility } from './PermissionRole.js';
import { PermissionsResourceType } from '@stamhoofd/types/PermissionsResourceType';
import { StockReservation } from './StockReservation.js';
export var GroupStatus;
(function (GroupStatus) {
    GroupStatus["Open"] = "Open";
    GroupStatus["Closed"] = "Closed";
    /**
     * @deprecated
     */
    GroupStatus["Archived"] = "Archived";
})(GroupStatus || (GroupStatus = {}));
export function getGroupStatusName(status, startDate) {
    switch (status) {
        case GroupStatus.Open:
            return $t(`%1EN`);
        case GroupStatus.Closed:
            return $t(`%1PH`);
        case GroupStatus.Archived:
            return $t(`%1Pg`);
        case 'RegistrationStartDate': {
            if (startDate) {
                return $t('%kR', { date: Formatter.startDate(startDate) });
            }
            return $t('%1VH');
        }
    }
}
export class Group extends AutoEncoder {
    static createDefaultResponsibilityPermissions(group, responsibility) {
        return PermissionRoleForResponsibility.create({
            name: responsibility.name + (group ? ` van ${group.settings.name.toString()}` : ''),
            responsibilityId: responsibility.id,
            responsibilityGroupId: group?.id ?? null,
        });
    }
    id;
    type = GroupType.Membership;
    organizationId = '';
    eventId;
    periodId = '';
    defaultAgeGroupId = null;
    waitingList = null;
    stockReservations = [];
    /**
     * @deprecated
     */
    cycle = 0;
    settings = GroupSettings.create({});
    createdAt = new Date();
    deletedAt = null;
    /**
     * Only set when you have access to this information
     */
    privateSettings = null;
    /**
     * Manually close a group
     */
    status = GroupStatus.Open;
    // Permission checking cache:
    event = null;
    // Permission checking cache (waiting list -> group -> event)
    parentGroup = null;
    getDiffName() {
        return this.settings.name;
    }
    static defaultSort(a, b) {
        if (a.settings.maxAge && !b.settings.maxAge) {
            return -1;
        }
        if (b.settings.maxAge && !a.settings.maxAge) {
            return 1;
        }
        if (!b.settings.maxAge && !a.settings.maxAge) {
            // name
            return Group.nameSort(a, b);
        }
        if (a.settings.maxAge > b.settings.maxAge) {
            return 1;
        }
        if (a.settings.maxAge < b.settings.maxAge) {
            return -1;
        }
        return Group.nameSort(a, b);
    }
    static nameSort(a, b) {
        if (a.settings.name.toLowerCase() < b.settings.name.toLowerCase()) {
            return -1;
        }
        if (a.settings.name.toLowerCase() > b.settings.name.toLowerCase()) {
            return 1;
        }
        return 0;
    }
    getMemberCount() {
        return this.settings.getMemberCount();
    }
    /**
     * Return the pre registration date only if is is active right now
     */
    get activePreRegistrationDate() {
        return this.settings.activePreRegistrationDate;
    }
    /**
     * Closed now, but will open in the future
     */
    get notYetOpen() {
        return this.settings.notYetOpen;
    }
    /**
     * No registrations and waiting list registrations are possible if closed
     */
    get closed() {
        if (this.status !== GroupStatus.Open) {
            return true;
        }
        if (this.settings.notYetOpen) {
            // Start date or pre registration date are in the future
            return true;
        }
        const now = new Date();
        if (this.settings.registrationEndDate && this.settings.registrationEndDate < now) {
            return true;
        }
        return false;
    }
    /**
     * Checks whether memebrs can register earlier for the passed group than for this group.
     * Useful for checking whether a waiting list opens earlier than the normal group, which should be signaled to admins.
     */
    isMoreRestrictiveOpenThan(group) {
        if (this.status === GroupStatus.Closed) {
            if (group.status !== GroupStatus.Closed) {
                return true;
            }
            return false;
        }
        if (group.status === GroupStatus.Closed) {
            return false;
        }
        if (this.settings.registrationStartDate) {
            if (!group.settings.registrationStartDate) {
                return true;
            }
            if (group.settings.registrationStartDate < this.settings.registrationStartDate) {
                return true;
            }
            return false;
        }
        if (this.settings.registrationEndDate) {
            if (!group.settings.registrationEndDate) {
                return true;
            }
            if (group.settings.registrationEndDate > this.settings.registrationEndDate) {
                return true;
            }
            return false;
        }
        return false;
    }
    trimmedName(categoryName) {
        const name = this.settings.name.toString();
        if (name.toLocaleLowerCase().startsWith(categoryName.toLocaleLowerCase())) {
            const cname = Formatter.capitalizeFirstLetter(Formatter.unwrapLeadingParentheses(name.slice(categoryName.length).trim()));
            if (cname.length) {
                return cname;
            }
        }
        return name;
    }
    /**
     * Returns all parent and grandparents of this group
     */
    getParentCategories(all, recursive = true) {
        const map = new Map();
        const parents = all.filter(g => g.groupIds.includes(this.id));
        for (const parent of parents) {
            map.set(parent.id, parent);
            if (recursive) {
                const hisParents = parent.getParentCategories(all);
                for (const pp of hisParents) {
                    map.set(pp.id, pp);
                }
            }
        }
        return [...map.values()];
    }
    hasAccess(permissions, allCategories, permissionLevel = PermissionLevel.Read) {
        if (!permissions) {
            return false;
        }
        if (permissions.hasResourceAccess(PermissionsResourceType.Groups, this.id, permissionLevel)) {
            return true;
        }
        // Check parent categories
        const parentCategories = this.getParentCategories(allCategories);
        for (const category of parentCategories) {
            if (permissions.hasResourceAccess(PermissionsResourceType.GroupCategories, category.id, permissionLevel)) {
                return true;
            }
        }
        return false;
    }
    isPublic(allCategories) {
        for (const parent of this.getParentCategories(allCategories)) {
            if (!parent.settings.public) {
                return false;
            }
        }
        return true;
    }
    hasReadAccess(permissions, allCategories) {
        return this.hasAccess(permissions, allCategories, PermissionLevel.Read);
    }
    hasWriteAccess(permissions, allCategories) {
        return this.hasAccess(permissions, allCategories, PermissionLevel.Write);
    }
    hasFullAccess(permissions, allCategories) {
        return this.hasAccess(permissions, allCategories, PermissionLevel.Full);
    }
    /**
     * True when the user has any grant relevant to this group — a level, an access right, or a grant on a parent category.
     * Used for coarse gates like "does this user have any reason to reach this group at all?".
     */
    hasSomeAccess(permissions, allCategories) {
        if (!permissions) {
            return false;
        }
        if (permissions.level !== PermissionLevel.None || permissions.accessRights.length > 0) {
            return true;
        }
        const groupPerm = permissions.getResourcePermissions(PermissionsResourceType.Groups, this.id);
        if (groupPerm && !groupPerm.isEmpty) {
            return true;
        }
        for (const category of this.getParentCategories(allCategories)) {
            const categoryPerm = permissions.getResourcePermissions(PermissionsResourceType.GroupCategories, category.id);
            if (categoryPerm && !categoryPerm.isEmpty) {
                return true;
            }
        }
        return false;
    }
    get squareImage() {
        return this.settings.squarePhoto ?? this.settings.coverPhoto;
    }
    get dateRange() {
        if (this.type === GroupType.EventRegistration) {
            return Formatter.dateRange(this.settings.startDate, this.settings.endDate);
        }
        return this.settings.period?.nameShort ?? null;
    }
    /**
     * Return the period ids that we consider as active for a given
     * group and organization combination.
     */
    getActivePeriodIds(organization) {
        return getActivePeriodIds(this.periodId, organization);
    }
    /**
     * Return the period ids that we consider as active for a given
     * group and organization combination.
     */
    getActivePeriods(organization) {
        return getActivePeriods(this.settings.period ?? { id: this.periodId, name: $t('%8X') }, organization);
    }
    getRecommendedFilter(organization) {
        const filter = [];
        const periods = this.getActivePeriods(organization ?? null).map((p) => {
            return {
                $: '$rel',
                value: p.id,
                name: p.name,
            };
        });
        const periodIdFilter = periods.length === 1 ? { periodId: periods[0] } : { periodId: { $in: periods } };
        if (this.settings.minAge !== null) {
            filter.push({
                age: {
                    $gt: this.settings.minAge - 1,
                },
            });
        }
        if (this.settings.maxAge !== null) {
            filter.push({
                age: {
                    $lt: this.settings.maxAge + 1,
                },
            });
        }
        if (this.settings.genderType === GroupGenderType.OnlyMale) {
            filter.push({
                gender: Gender.Male,
            });
        }
        if (this.settings.genderType === GroupGenderType.OnlyFemale) {
            filter.push({
                gender: Gender.Male,
            });
        }
        if (this.settings.requireGroupIds.length) {
            filter.push({
                registrations: {
                    $elemMatch: {
                        groupId: {
                            $in: this.settings.requireGroupIds,
                        },
                    },
                },
            });
        }
        if (this.settings.preventGroupIds.length) {
            filter.push({
                registrations: {
                    $elemMatch: {
                        groupId: {
                            $not: {
                                $in: this.settings.preventGroupIds,
                            },
                        },
                    },
                },
            });
        }
        if (this.settings.requireDefaultAgeGroupIds.length) {
            filter.push({
                registrations: {
                    $elemMatch: {
                        $and: [
                            periodIdFilter,
                            {
                                group: {
                                    defaultAgeGroupId: {
                                        $in: this.settings.requireDefaultAgeGroupIds,
                                    },
                                },
                            },
                        ],
                    },
                },
            });
        }
        else {
            if (this.settings.requirePlatformMembershipOn !== null) {
                const requirePlatformMembershipOn = this.settings.requirePlatformMembershipOn;
                filter.push({
                    $or: [
                        {
                            platformMemberships: {
                                $elemMatch: {
                                    startDate: {
                                        $lte: requirePlatformMembershipOn,
                                    },
                                    endDate: {
                                        $gt: requirePlatformMembershipOn,
                                    },
                                },
                            },
                        }, {
                            platformMemberships: {
                                $elemMatch: {
                                    startDate: {
                                        $lte: { $: '$now' },
                                    },
                                    endDate: {
                                        $gt: { $: '$now' },
                                    },
                                },
                            },
                        },
                    ],
                });
            }
            else if (this.settings.requirePlatformMembershipOnRegistrationDate) {
                filter.push({
                    platformMemberships: {
                        $elemMatch: {
                            startDate: {
                                $lte: { $: '$now' },
                            },
                            endDate: {
                                $gt: { $: '$now' },
                            },
                        },
                    },
                });
            }
        }
        if (STAMHOOFD.userMode === 'platform') {
            if (organization && (!this.settings.requireOrganizationIds.length || this.settings.requireOrganizationIds.includes(organization.id))) {
                filter.push({
                    registrations: {
                        $elemMatch: {
                            $and: [
                                periodIdFilter,
                                {
                                    organizationId: organization.id,
                                },
                            ],
                        },
                    },
                });
            }
            else if (this.settings.requireOrganizationIds.length) {
                filter.push({
                    registrations: {
                        $elemMatch: {
                            $and: [
                                periodIdFilter,
                                {
                                    organizationId: {
                                        $in: this.settings.requireOrganizationIds,
                                    },
                                },
                            ],
                        },
                    },
                });
            }
            if (this.settings.requireOrganizationTags.length) {
                filter.push({
                    registrations: {
                        $elemMatch: {
                            $and: [
                                periodIdFilter,
                                {
                                    organization: {
                                        tags: {
                                            $in: this.settings.requireOrganizationTags,
                                        },
                                    },
                                },
                            ],
                        },
                    },
                });
            }
        }
        else {
            filter.push({
                registrations: {
                    $elemMatch: { $and: [periodIdFilter] },
                },
            });
        }
        return filter;
    }
    getTags(options) {
        const tags = [];
        const now = options.now ?? new Date();
        const remainingStock = this.settings.getRemainingStockIncludingPricesAndOptions(this);
        const activePreRegistrationDate = this.activePreRegistrationDate;
        const preRegistrations = activePreRegistrationDate !== null;
        const allWaitingList = this.settings.waitingListType === WaitingListType.All;
        if (remainingStock !== 0 && !allWaitingList) {
            if (activePreRegistrationDate !== null && (remainingStock === null || remainingStock > 0) && activePreRegistrationDate > now) {
                tags.push({
                    icon: 'calendar',
                    title: $t('%1Y7', { date: Formatter.startDate(activePreRegistrationDate) }),
                    style: 'warn',
                });
            }
            else if (preRegistrations && (remainingStock === null || remainingStock > 0)) {
                tags.push({
                    icon: 'calendar',
                    title: $t('%1bt', { date: Formatter.endDate(this.settings.registrationStartDate ?? new Date()) }),
                    style: 'warn',
                });
            }
            if (this.notYetOpen) {
                tags.push({
                    icon: 'calendar',
                    title: $t('%1Z9', { date: Formatter.startDate(this.settings.registrationStartDate ?? new Date()) }),
                    style: 'warn',
                });
            }
        }
        if (options.app === 'dashboard') {
            if (this.status === GroupStatus.Closed) {
                tags.push({
                    icon: 'lock',
                    title: $t('%CZ'),
                    style: 'error',
                });
            }
            else if (this.settings.registrationEndDate && this.closed && !this.notYetOpen) {
                tags.push({
                    icon: 'lock',
                    title: $t('%1Ve', { date: Formatter.endDate(this.settings.registrationEndDate) }),
                    style: 'error',
                });
            }
            else if (options.organization && options.organization.id === this.organizationId) {
                if (this.periodId !== options.organization.period.period.id) {
                    tags.push({
                        icon: 'lock',
                        title: $t('%ZZw'),
                        style: 'error',
                    });
                }
                else if (this.settings.period?.locked) {
                    tags.push({
                        icon: 'lock',
                        title: $t('%ZZu'),
                        style: 'error',
                    });
                }
            }
        }
        if (remainingStock !== 0 && !allWaitingList) {
            if ((!this.closed || (this.status !== GroupStatus.Closed && options.app === 'dashboard')) && this.settings.registrationEndDate && (options.app === 'dashboard' || this.settings.registrationEndDate < new Date(now.getTime() + 1_000 * 60 * 60 * 24 * 31))) {
                tags.push({
                    icon: 'lock',
                    title: $t('%1d3', { date: Formatter.endDate(this.settings.registrationEndDate) }),
                    style: 'warn',
                });
            }
            if (tags.length === 0 && !this.closed && options.app === 'dashboard') {
                tags.push({
                    icon: 'earth',
                    title: $t('%1EN'),
                    style: 'success',
                });
            }
            if (!this.closed && options.app === 'dashboard' && this.type === GroupType.Membership && options.blockCreatingNewMembers) {
                tags.push({
                    icon: 'disabled',
                    title: $t('%ZdL'),
                    style: 'warn',
                });
            }
        }
        if ((!this.closed || this.notYetOpen) && (remainingStock !== null && (remainingStock < 50 || options.app === 'dashboard'))) {
            if (remainingStock > 0) {
                tags.push({
                    icon: 'user',
                    title: remainingStock !== 1
                        ? $t('%1d9', { count: remainingStock })
                        : $t('%Zfj'),
                    style: 'warn',
                });
            }
            else if (this.waitingList !== null && !this.waitingList.closed) {
                if (this.waitingList.settings.registrationEndDate && (options.app === 'dashboard' || this.waitingList.settings.registrationEndDate < new Date(now.getTime() + 1_000 * 60 * 60 * 24 * 31))) {
                    tags.push({
                        icon: 'clock',
                        title: $t('%Zfg', { date: Formatter.endDate(this.waitingList.settings.registrationEndDate) }),
                        style: 'error',
                    });
                }
                else {
                    tags.push({
                        icon: 'clock',
                        title: $t('%Zfu'),
                        style: 'error',
                    });
                }
            }
            else if (this.waitingList !== null && this.waitingList.notYetOpen && this.waitingList.settings.registrationStartDate) {
                if (this.waitingList.settings.registrationEndDate && (options.app === 'dashboard' || this.waitingList.settings.registrationEndDate < new Date(now.getTime() + 1_000 * 60 * 60 * 24 * 31))) {
                    tags.push({
                        icon: 'clock',
                        title: $t('%Zfs', {
                            'start-date': Formatter.startDate(this.waitingList.settings.registrationStartDate),
                            'end-date': Formatter.endDate(this.waitingList.settings.registrationStartDate),
                        }),
                        style: 'error',
                    });
                }
                else {
                    tags.push({
                        icon: 'clock',
                        title: $t('%Zfl', { date: Formatter.startDate(this.waitingList.settings.registrationStartDate) }),
                        style: 'error',
                    });
                }
            }
            else {
                tags.push({
                    icon: 'disabled',
                    title: $t('%Um'),
                    style: 'error',
                });
            }
        }
        else if ((allWaitingList || this.closed) && this.waitingList !== null) {
            if (!this.waitingList.closed) {
                if (this.waitingList.settings.registrationEndDate && (options.app === 'dashboard' || this.waitingList.settings.registrationEndDate < new Date(now.getTime() + 1_000 * 60 * 60 * 24 * 31))) {
                    tags.push({
                        icon: 'clock',
                        title: $t('%Zfk', { date: Formatter.endDate(this.waitingList.settings.registrationEndDate) }),
                        style: 'error',
                    });
                }
                else {
                    tags.push({
                        icon: 'clock',
                        title: $t('%1ZU'),
                        style: 'error',
                    });
                }
            }
            else if ((!this.notYetOpen || allWaitingList || (options.app === 'dashboard' && this.isMoreRestrictiveOpenThan(this.waitingList))) && this.waitingList.notYetOpen && this.waitingList.settings.registrationStartDate) {
                if (this.waitingList.settings.registrationEndDate && (options.app === 'dashboard' || this.waitingList.settings.registrationEndDate < new Date(now.getTime() + 1_000 * 60 * 60 * 24 * 31))) {
                    tags.push({
                        icon: 'clock',
                        title: $t('%Zff', {
                            'start-date': Formatter.startDate(this.waitingList.settings.registrationStartDate),
                            'end-date': Formatter.endDate(this.waitingList.settings.registrationEndDate),
                        }),
                        style: 'error',
                    });
                }
                else {
                    tags.push({
                        icon: 'clock',
                        title: $t('%Zfh', { date: Formatter.startDate(this.waitingList.settings.registrationStartDate) }),
                        style: 'error',
                    });
                }
            }
        }
        return tags;
    }
    static decodeField(...args) {
        const result = super.decodeField.call(this, ...args);
        // Create circular reference for permission checking in the frontend
        if (result.waitingList && result.type === GroupType.EventRegistration) {
            result.waitingList.parentGroup = result;
        }
        return result;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Group.prototype, "id", void 0);
__decorate([
    field({ decoder: new EnumDecoder(GroupType), version: 286 })
], Group.prototype, "type", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 250 })
], Group.prototype, "organizationId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 407 })
], Group.prototype, "eventId", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 265 })
], Group.prototype, "periodId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 267 })
], Group.prototype, "defaultAgeGroupId", void 0);
__decorate([
    field({ decoder: Group, nullable: true, version: 292 })
], Group.prototype, "waitingList", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StockReservation), version: 298 })
], Group.prototype, "stockReservations", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], Group.prototype, "cycle", void 0);
__decorate([
    field({ decoder: GroupSettings })
], Group.prototype, "settings", void 0);
__decorate([
    field({ decoder: DateDecoder, version: 187 })
], Group.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true, version: 187 })
], Group.prototype, "deletedAt", void 0);
__decorate([
    field({ decoder: GroupPrivateSettings, nullable: true, version: 10 })
], Group.prototype, "privateSettings", void 0);
__decorate([
    field({ decoder: new EnumDecoder(GroupStatus), version: 192 })
], Group.prototype, "status", void 0);
//# sourceMappingURL=Group.js.map